import { Box } from '@mui/material'
import { isFuture } from 'date-fns'
import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { Wrap } from 'shared/components/Base/Wrap'
import { Button } from 'shared/components/Button/Button'
import { DateInput } from 'shared/components/DateInput/DateInput'
import { DropZoneArea } from 'shared/components/DropZoneArea/DropZoneArea'
import { Text } from 'shared/components/Text/Text'
import { Handler, NoNullFields, noNullFields } from 'shared/helpers/typeHelper'
import { saveMedicalCard } from 'shared/models/user'
import { sizes } from 'shared/theme'
import { midGray } from 'shared/theme/colors'

import { User } from '../../../models/user'
import { useUserProvider } from '../../Context/UserContext'

interface MedicalCardProps {
  nextStep: Handler
  user: User
}

interface Form {
  uploadedFile: File | null
  expirationDate: string
}

export function MedicalCard({ nextStep, user }: MedicalCardProps) {
  const [isLoading, setIsLoading] = useState(false)
  const { showBanner } = useUserProvider()
  const { handleSubmit, control } = useForm<Form>({
    defaultValues: {
      uploadedFile: null,
      expirationDate: user.driver.medicalCardDocument?.dateExpired || '',
    },
    mode: 'onSubmit',
  })

  async function onSubmit(data: NoNullFields<Form>) {
    setIsLoading(true)

    try {
      const formData = new FormData()
      // if medical card is uploaded, skip
      if (!data.uploadedFile && user.driver.medicalCard) {
        return nextStep()
      }

      if (data.uploadedFile) {
        formData.append('medical_card', data.uploadedFile)
      }
      formData.append('medical_card_expiration', data.expirationDate)
      await saveMedicalCard({
        formData: formData,
        userId: user.id,
      })
      nextStep()
    } catch (err: any) {
      showBanner('error', err)
    }
    setIsLoading(false)
  }

  return (
    <form onSubmit={handleSubmit((data) => onSubmit(noNullFields(data)))}>
      <Text variant="header2" align="center">
        Upload your medical certificate
      </Text>
      <Text variant="paragraph1" align="center" color={midGray}>
        If you’re over 65, provide your full long form medical examination report.
      </Text>
      <Box mt={5} />

      <Wrap>
        <Controller
          render={({ field, fieldState }) => (
            <DropZoneArea {...field} hasError={!!fieldState.error} uploadedFile={user.driver.medicalCard} />
          )}
          name="uploadedFile"
          control={control}
          rules={{
            validate: (v) => {
              if (user.driver.medicalCard) return true
              if (!v) return false
              return true
            },
          }}
        />

        <Box mt={1} />
        <Controller
          render={({ field, fieldState }) => (
            <DateInput
              {...field}
              label="Expiration Date of Medical Card"
              error={!!fieldState.error}
              noMargin
              shouldNotForwardRef
            />
          )}
          name="expirationDate"
          control={control}
          rules={{
            validate: (v) => {
              if (!v || !isFuture(new Date(v))) return false
              return true
            },
          }}
        />
      </Wrap>

      <Box mt={5} />
      <Button type="submit" size={sizes.extraLarge} isLoading={isLoading} margin="0 auto">
        Submit
      </Button>
      <Box mt={4} />
      <Button variant="text" onClick={nextStep} margin="0 auto">
        Upload later
      </Button>
    </form>
  )
}
